/**
 * `brandKey`: one stable key per brand across the spellings AI answers use.
 *
 * The recommendation-category judge stores every brand an answer names —
 * tracked or not — keyed by `normalizeBrandKey(name)`, so "HubSpot",
 * "HubSpot, Inc." and "hubspot.com" land on one leaderboard row. Built on the
 * canonical mention normalization (`normalizeBrandMentionText`: diacritics,
 * case, markdown, `&` vs "and", apostrophes, punctuation, trailing legal
 * suffixes), plus two brand-name specifics: a trailing parenthetical
 * descriptor ("Ramp (best for startups)") and a trailing web suffix
 * ("Monday.com", "Jasper.ai") are dropped.
 *
 * The key is a lowercase, hyphen-joined slug ("microsoft-dynamics-365"), safe
 * in URLs. It identifies a SPELLING FAMILY, not a tracked entity: whether a
 * key is the focal brand or a tracked competitor is always re-resolved at read
 * time with `resolveMention`.
 *
 * Pure: safe for `@repo/core/client`.
 */

import { normalizeBrandMentionText } from "./brand-mention-match";

/** Web suffixes dropped from the end of a brand name ("Monday.com"). */
const WEB_SUFFIX = /\.(?:com|io|ai|co|net|org|app|so)$/i;

/** A trailing "(...)" descriptor: "Ramp (best for startups)". */
const TRAILING_PARENTHETICAL = /\s*\([^()]*\)\s*$/;

/**
 * Normalize a brand name to its key. Returns "" for empty or
 * punctuation-only input (callers drop those).
 */
export function normalizeBrandKey(name: string | null | undefined): string {
	if (!name) {
		return "";
	}
	let value = name.trim();
	// Strip wrapping markdown/quotes first so a suffix check sees the name.
	value = value.replace(/^[*_`"'“”‘’]+|[*_`"'“”‘’]+$/g, "").trim();
	// A descriptor in parentheses is never part of the name — unless it IS the
	// whole name, which the emptiness check below guards.
	const withoutParen = value.replace(TRAILING_PARENTHETICAL, "").trim();
	if (withoutParen) {
		value = withoutParen;
	}
	const withoutSuffix = value.replace(WEB_SUFFIX, "").trim();
	if (withoutSuffix) {
		value = withoutSuffix;
	}
	const normalized = normalizeBrandMentionText(value);
	return normalized ? normalized.split(" ").join("-") : "";
}
